import React, { useState, useRef, useEffect } from "react";
import { getVideoCall } from "../services/auth";

const FeedBack = ({ feedBackdata }) => {
  const [activeTab, setActiveTab] = useState("report");
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Recording URLs + loading/error state
  const [recordingUrls, setRecordingUrls] = useState({ audio: null, video: null });
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [recordingFetched, setRecordingFetched] = useState(false); // avoid refetching every tab click

  const fetchVideo = async () => {
    if (!feedBackdata?.callId) return;
    setRecordingLoading(true);
    setRecordingError(null);
    try {
      const response = await getVideoCall(feedBackdata.callId);
      if (response?.data?.status) {
        setRecordingUrls({
          audio: response.data.audio || null,
          video: response.data.video || null,
        });
      } else {
        setRecordingError("Recording not available.");
      }
    } catch (error) {
      console.error("Error fetching recording:", error);
      setRecordingError("Failed to load recording. Please try again.");
    } finally {
      setRecordingLoading(false);
      setRecordingFetched(true);
    }
  };

  // Fetch only when the Recordings tab is opened, and only once
  // (presigned URLs are valid for 30 min — re-fetch if you expect longer sessions)
  useEffect(() => {
    if (activeTab === "recordings" && !recordingFetched) {
      fetchVideo();
    }
  }, [activeTab]);

  // Volume control — unrelated to fetching, kept separate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressChange = (e) => {
    if (audioRef.current) {
      const newTime = (e.target.value / 100) * duration;
      audioRef.current.currentTime = newTime;
      setProgress(parseInt(e.target.value));
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const ScoreCircle = ({ score, maxScore }) => {
    const percentage = (score / maxScore) * 100;
    const circumference = 2 * Math.PI * 45;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="position-relative d-inline-block" style={{ width: "96px", height: "96px" }}>
        <svg width="96" height="96" style={{ transform: "rotate(-90deg)" }} viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" stroke="#e9ecef" strokeWidth="8" fill="transparent" />
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="#6f42c1"
            strokeWidth="8"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: "all 0.7s ease" }}
          />
        </svg>
        <div className="position-absolute top-50 start-50 translate-middle">
          <span className="h4 fw-bold mb-0">{score}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container">
        <div className="col-lg-10 mx-auto">
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "report" ? "active" : ""}`}
                onClick={() => setActiveTab("report")}
              >
                Report
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "recordings" ? "active" : ""}`}
                onClick={() => setActiveTab("recordings")}
              >
                Recordings
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "transcript" ? "active" : ""}`}
                onClick={() => setActiveTab("transcript")}
              >
                Transcript
              </button>
            </li>
          </ul>

          <div>
            {/* REPORT TAB */}
            {activeTab === "report" && (
              <>
                <div className="row align-items-center mb-4">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-award text-warning me-2 fs-5"></i>
                      <span className="text-muted me-2">Overall Impression:</span>
                      <span className="fw-bold">{feedBackdata?.feedback?.marks ?? 0}/100</span>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-muted me-2">Recommend for Hire:</span>
                    {feedBackdata?.feedback?.recommendForHire === "Yes" ? (
                      <span className="fw-bold text-success">Yes</span>
                    ) : (
                      <span className="fw-bold text-danger">No</span>
                    )}
                  </div>
                  <div className="col-md-5">
                    <span className="text-muted me-2">Call Ended Reason:</span>
                    <span className="fw-bold">{feedBackdata?.endReason || "Not Provided"}</span>
                  </div>
                </div>

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <ScoreCircle score={feedBackdata?.feedback?.marks} maxScore={100} />
                      </div>
                      <div className="col">
                        <p className="mb-0 text-muted lead">
                          {feedBackdata?.feedback?.finalAssessment}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h2 className="h3 fw-bold mb-3">Breakdown of the Interview:</h2>
                  {feedBackdata?.feedback?.categoryScores.map((item, index) => (
                    <div key={index} className="card border-0 shadow-sm mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h3 className="h5 mb-0">{index + 1}. {item.name}</h3>
                          <span className="badge bg-primary fs-6">({item.score}/100)</span>
                        </div>
                        <div className="progress mb-3" style={{ height: "8px" }}>
                          <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${item.score}%` }}></div>
                        </div>
                        <p className="text-muted mb-0">{item.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <h2 className="h3 fw-bold mb-3">Strengths</h2>
                  {feedBackdata?.feedback?.strengths.length === 0 ? (
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        <p className="text-muted fst-italic mb-0">No significant strengths identified.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      {feedBackdata?.feedback?.strengths.map((strength, index) => (
                        <div key={index} className="col-12 mb-2">
                          <div className="card border-0 border-start border-success border-4 bg-light">
                            <div className="card-body py-2">
                              <p className="text-success mb-0">• {strength}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h2 className="h3 fw-bold mb-3">Areas for Improvement</h2>
                  {feedBackdata?.feedback?.areasForImprovement?.map((improvement, index) => (
                    <div key={index} className="col-12 mb-2">
                      <div className="card border-0 border-start border-warning border-4 bg-light">
                        <div className="card-body py-2">
                          <p className="text-warning-emphasis mb-0">• {improvement}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {feedBackdata?.feedback?.summary && (
                  <div className="mb-4">
                    <h2 className="h3 fw-bold mb-3">Summary</h2>
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col">
                            <p className="mb-0 text-muted lead">{feedBackdata.feedback.summary}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* RECORDINGS TAB */}
            {activeTab === "recordings" && (
              <div className="row">
                {recordingLoading && (
                  <div className="col-12 text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading recordings...</p>
                  </div>
                )}

                {!recordingLoading && recordingError && (
                  <div className="col-12">
                    <div className="alert alert-danger d-flex justify-content-between align-items-center">
                      <span>{recordingError}</span>
                      <button className="btn btn-sm btn-outline-danger" onClick={fetchVideo}>
                        Retry
                      </button>
                    </div>
                  </div>
                )}

                {!recordingLoading && !recordingError && (
                  <>
                    <div className="col-md-6 mb-4">
                      {recordingUrls.video ? (
                        <>
                          <h5 className="mb-3">Recorded Video</h5>
                          <video width="100%" height="auto" controls key={recordingUrls.video}>
                            <source src={recordingUrls.video} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </>
                      ) : (
                        <p className="text-muted">No video recording available.</p>
                      )}
                    </div>

                    <div className="col-md-6 mb-4">
                      {recordingUrls.audio ? (
                        <>
                          <h5 className="mb-3">Recorded Audio</h5>
                          <audio
                            ref={audioRef}
                            src={recordingUrls.audio}
                            onLoadedMetadata={() => setDuration(audioRef.current.duration)}
                            onTimeUpdate={() => {
                              if (audioRef.current) {
                                setCurrentTime(audioRef.current.currentTime);
                                setProgress((audioRef.current.currentTime / duration) * 100 || 0);
                              }
                            }}
                            onEnded={() => setIsPlaying(false)}
                            controls={false}
                          >
                            <source src={recordingUrls.audio} type="audio/wav" />
                            Your browser does not support the audio tag.
                          </audio>
                          <div className="custom-audio-controls card p-3 shadow-sm mt-2">
                            <div className="d-flex align-items-center mb-2">
                              <button
                                className="btn btn-primary rounded-circle me-3"
                                onClick={togglePlayPause}
                                style={{ width: "40px", height: "40px", display: "flex", justifyContent: "center", alignItems: "center" }}
                              >
                                <i className={`bi ${isPlaying ? "bi-pause-fill" : "bi-play-fill"} fs-5`}></i>
                              </button>
                              <div className="flex-grow-1 me-3">
                                <input
                                  type="range"
                                  className="form-range"
                                  min="0"
                                  max="100"
                                  value={progress}
                                  onChange={handleProgressChange}
                                />
                              </div>
                              <span className="text-muted small">
                                {formatTime(currentTime)} / {formatTime(duration)}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-volume-up-fill me-2 fs-5 text-muted"></i>
                              <input
                                type="range"
                                className="form-range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={volume}
                                onChange={handleVolumeChange}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-muted">No audio recording available.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* TRANSCRIPT TAB */}
            {activeTab === "transcript" && (
              <div
                style={{
                  height: "60vh",
                  border: "1px solid #dee2e6",
                  borderRadius: "5px",
                  overflowY: "auto",
                  backgroundColor: "#ffffff",
                  color: "#212529",
                  fontSize: "14px",
                }}
                className="p-3"
              >
                {feedBackdata?.feedback?.transcript?.map((item, index) => (
                  <div key={index} className="mb-3">
                    {item?.role === "assistant" ? (
                      <div className="text-start">
                        <div className="bg-light p-2 rounded-3 d-inline-block shadow-sm">
                          <span className="text-primary fw-bold">Assistant:</span> {item?.content}
                        </div>
                      </div>
                    ) : (
                      <div className="text-end">
                        <div className="bg-primary text-white p-2 rounded-3 d-inline-block shadow-sm">
                          <span className="fw-bold">Candidate:</span> {item?.content}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="text-center mt-4">
                  <div className="bg-danger text-white px-3 py-2 rounded-pill d-inline-block shadow-sm">
                    <i className="bi bi-telephone-x me-2"></i>
                    Call Ended
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap-icons/1.10.3/font/bootstrap-icons.min.css" rel="stylesheet" />
      <style jsx>{`
        .text-warning-emphasis { color: #997404 !important; }
        .form-range::-webkit-slider-thumb { background-color: #0d6efd; }
        .form-range::-moz-range-thumb { background-color: #0d6efd; }
        .form-range { height: 0.5rem; }
      `}</style>
    </div>
  );
};

export default FeedBack;